import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { GitPullRequestArrow, RefreshCw } from "lucide-react"
import { PageHeader, Panel, Section } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { useIsDark } from "@/features/ai-matcher/use-is-dark"
import { productImageUrl } from "@/features/catalogue-review/CardImage"
import {
	gameName,
	groupFlag,
	nf,
} from "@/features/catalogue-review/review-copy"
import {
	useProposals,
	useQuietGames,
	useRebuildReview,
	useReviewOverview,
} from "@/features/catalogue-review/use-review"
import { cn } from "@/utils/styles.helpers"

export const Route = createFileRoute("/catalogue-v2/")({
	component: CatalogueReviewIndex,
})

const OPEN_STATUSES = new Set(["submitted", "changes_requested"])

function GameIcon(props: { icon: string | null }) {
	if (!props.icon) {
		return <span aria-hidden className="size-5 shrink-0" />
	}

	return (
		<img
			src={productImageUrl(props.icon, 40)}
			alt=""
			className="size-5 shrink-0 rounded-[3px] object-contain"
			loading="lazy"
		/>
	)
}

/** A count cell: tabular, right aligned, grey when zero. */
function CountCell(props: {
	value: number
	tone?: "warn" | "bad" | "info"
	title?: string
}) {
	const toneClass =
		props.value === 0
			? "text-meta"
			: props.tone === "warn"
				? "text-warn"
				: props.tone === "bad"
					? "text-bad"
					: props.tone === "info"
						? "text-primary-text"
						: undefined

	return (
		<TableCell
			className={cn("text-right tabular-nums", toneClass)}
			title={props.title}
		>
			{nf(props.value)}
		</TableCell>
	)
}

function CatalogueReviewIndex() {
	const overview = useReviewOverview()
	const rebuild = useRebuildReview()
	const proposals = useProposals()
	const quiet = useQuietGames()
	const isDark = useIsDark()
	const navigate = useNavigate()

	const open = (proposals.data ?? []).filter((p) => OPEN_STATUSES.has(p.status))
	const openByGame = new Map<string, number>()
	for (const p of open) {
		openByGame.set(p.gameSlug, (openByGame.get(p.gameSlug) ?? 0) + 1)
	}

	const games = overview.data ?? []
	const quietGames = quiet.data ?? []

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Catalogue review"
				count={overview.data ? games.length : undefined}
				description="Every pending catalogue change, grouped into decisions, built from the same staging data as the diff view."
				actions={
					<>
						<Button asChild variant="outline" size="sm">
							<Link to="/catalogue-v2/proposals">
								<GitPullRequestArrow />
								Propositions
								{open.length > 0 ? (
									<span className="text-xs text-meta tabular-nums">
										{open.length} open
									</span>
								) : null}
							</Link>
						</Button>
						<Button
							variant="outline"
							size="sm"
							disabled={rebuild.isPending}
							title="Recompute every game's model from the current staging data. Decisions survive."
							onClick={() => rebuild.mutate(undefined)}
						>
							<RefreshCw className={cn(rebuild.isPending && "animate-spin")} />
							Rebuild all from staging
						</Button>
					</>
				}
			/>

			<Panel>
				<Table>
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<TableHead>Game</TableHead>
							<TableHead
								className="text-right"
								title="Changes the source proposes: additions, edits and removals combined."
							>
								Changes
							</TableHead>
							<TableHead
								className="text-right"
								title="Similar changes are grouped so each group is one decision."
							>
								Decisions to make
							</TableHead>
							<TableHead
								className="text-right"
								title="Groups that lose data or touch products people hold."
							>
								Need attention
							</TableHead>
							<TableHead
								className="text-right"
								title="Products the source no longer lists. Each one is decided row by row."
							>
								Proposed removals
							</TableHead>
							<TableHead
								className="text-right"
								title="Printing changes a person must decide: a removed printing, or one the TCGplayer SKU list has and nobody prices."
							>
								Printings to decide
							</TableHead>
							<TableHead
								className="text-right"
								title="Changes of locked expansions. They stay out of the review until the sets are unlocked."
							>
								Held by locks
							</TableHead>
							<TableHead className="text-right">
								Propositions awaiting review
							</TableHead>
							<TableHead className="text-right">Built</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{overview.isPending
							? [1, 2, 3, 4, 5, 6].map((i) => (
									<TableRow key={i} className="hover:bg-transparent">
										<TableCell colSpan={9} className="py-0">
											<Skeleton className="my-2 h-5 w-full" />
										</TableCell>
									</TableRow>
								))
							: null}

						{overview.data && games.length === 0 ? (
							<TableRow className="hover:bg-transparent">
								<TableCell colSpan={9} className="text-muted-foreground">
									No review model yet. Build it from the current staging data;
									nothing is written outside the review tables.
								</TableCell>
							</TableRow>
						) : null}

						{games.map((g) => {
							const raw = g.rawAdds + g.rawMods + g.rawDels
							const flagged = g.groups.filter((x) => groupFlag(x)).length
							const removedGroup = g.groups.find(
								(x) => x.groupKey === "life:removed",
							)
							const printingsToDecide =
								g.groups.find(
									(x) => x.groupKey === "field:product:finishes:modified",
								)?.impact?.needsFinishDecision ?? 0
							const name = g.gameName ?? gameName(g.gameSlug)
							const icon = isDark
								? (g.iconDark ?? g.iconLight)
								: (g.iconLight ?? g.iconDark)
							const openHere = openByGame.get(g.gameSlug) ?? 0

							return (
								<TableRow
									key={g.gameSlug}
									className="cursor-pointer"
									onClick={() =>
										navigate({
											to: "/catalogue-v2/$gameSlug",
											params: { gameSlug: g.gameSlug },
										})
									}
								>
									<TableCell>
										<Link
											to="/catalogue-v2/$gameSlug"
											params={{ gameSlug: g.gameSlug }}
											className="inline-flex items-center gap-2.5 font-medium hover:text-primary-text"
											onClick={(e) => e.stopPropagation()}
										>
											<GameIcon icon={icon} />
											{name}
										</Link>
									</TableCell>
									<CountCell value={raw} />
									<CountCell value={g.groups.length} />
									<CountCell value={flagged} tone="warn" />
									<CountCell value={removedGroup?.n ?? 0} tone="bad" />
									<CountCell value={printingsToDecide} tone="warn" />
									<CountCell value={g.heldCount ?? 0} />
									<CountCell value={openHere} tone="info" />
									<TableCell className="text-right text-xs text-meta tabular-nums">
										{g.generatedAt.slice(0, 16).replace("T", " ")}
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</Panel>

			{quietGames.length > 0 ? (
				<Section label={`Nothing pending · ${quietGames.length}`}>
					<div className="flex flex-wrap gap-x-5 gap-y-2 text-muted-foreground">
						{quietGames.map((g) => {
							const icon = isDark
								? (g.iconDark ?? g.iconLight)
								: (g.iconLight ?? g.iconDark)

							return (
								<span
									key={g.gameSlug}
									title="Staged data exists and the current import matches production. There is nothing to review."
									className="inline-flex items-center gap-2"
								>
									{icon ? (
										<img
											src={productImageUrl(icon, 32)}
											alt=""
											className="size-4 rounded-[2px] object-contain"
											loading="lazy"
										/>
									) : null}
									{g.gameName ?? g.gameSlug}
								</span>
							)
						})}
					</div>
				</Section>
			) : null}
		</div>
	)
}
